const { fetchBrowsingContext } = window.BAO.Utils
class ProductStockDrawer extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.revealTriggers()
  }

  setupListeners () {
    super.setupListeners()

    this.onOptionChange = this.onOptionChange.bind(this)

    this.els.option.elements.forEach(el => {
      this.listeners.add(el, 'change', this.onOptionChange)
    })
  }

  async onOptionChange () {
    await this.buildResults()
  }

  async revealTriggers () {
    const {
      detected_values: { country: { handle: detectedIso2Code = '' } } = {},
    } = await fetchBrowsingContext()
    const storeIso2Code = this.getAttribute('store-country')

    if (detectedIso2Code === storeIso2Code) {
      this.externalEls.trigger.elements.forEach(trigger => {
        trigger.style.display = 'flex'
      })
    }

    await this.buildResults()
  }

  async fetchResults () {
    // Store current variant
    const currentVariant = this.variant
    // If we have no variant, we have no results
    if (!currentVariant) return []

    const resultsKey = 'inventory-results'
    const resultString = window.sessionStorage.getItem(resultsKey) || '{}'
    const storedLocations = JSON.parse(resultString)

    if (storedLocations[currentVariant.id]) {
      return storedLocations[currentVariant.id]
    }

    // Fetch the results
    const resultRequest = await fetch(
      'https://the-frankie-shop.pages.dev/api/stock',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shop: this.storeDomain,
          variantId: currentVariant.id,
        }),
      },
    )

    // Return the results
    const results = await resultRequest.json()
    const locations = results.locations.filter(
      result => result.name.toLowerCase() !== 'office' && result.image !== null,
    )

    storedLocations[currentVariant.id] = locations
    window.sessionStorage.setItem(resultsKey, JSON.stringify(storedLocations))

    return locations
  }

  async buildResults () {
    const results = await this.fetchResults()
    const resultsWithQuantities = results.filter(result => result.quantity > 0)

    // Clear the results list
    this.els.results.element.innerHTML = ''

    // Loop results and build new result element for each
    results.forEach(result => {
      // Address is build from multiple components, filter out null values
      const resultAddress = [
        result.name,
        result.address.address1,
        result.address.address2,
        `${result.address.zip} ${result.address.city}`,
      ]
        .filter(i => i !== null)
        .join('<br>')

      // Build the result element
      const resultElement = this.buildResult(
        result.image,
        resultAddress,
        result.openingTime,
        result.link,
        result.quantity,
      )

      // Append the result element to the results list
      this.els.results.element.appendChild(resultElement)
    })

    this.els.message.element.innerText = this.getStoreAvailabilityString(
      resultsWithQuantities.length,
    )
  }

  buildResult (imageSrc, address, openingTime, detailsLink, quantity) {
    const resultDiv = document.createElement('div')
    resultDiv.className = 'drw-ProductStock_Result'

    const imageContainer = document.createElement('div')
    imageContainer.className = 'drw-ProductStock_ResultImageContainer'

    const imageUrl = imageSrc?.length ? new URL(imageSrc) : null
    if (imageUrl) {
      imageUrl.searchParams.set('width', 216)
    }

    const image = document.createElement('img')
    image.className = 'drw-ProductStock_ResultImage'
    image.src = imageUrl ? imageUrl.toString() : imageSrc
    image.width = '108'
    image.height = '151'
    image.alt = address.replace(/(<([^>]+)>)/gi, ',').replace(/,+/g, ', ')
    image.loading = 'lazy'

    const contentDiv = document.createElement('div')
    contentDiv.className = 'drw-ProductStock_ResultContent fz-12_133'

    const addressP = document.createElement('p')
    addressP.className = 'drw-ProductStock_ResultAddress'
    addressP.innerHTML = address

    const openingTimeP = document.createElement('p')
    openingTimeP.className = 'drw-ProductStock_ResultOpeningTime'
    openingTimeP.textContent = openingTime

    const availabilityP = document.createElement('p')
    availabilityP.className = `drw-ProductStock_Availability drw-ProductStock_Availability-${this.getStockState(
      quantity,
    )}`
    availabilityP.textContent = this.getAvailabilityString(quantity)

    const actionsDiv = document.createElement('div')
    actionsDiv.className = 'drw-ProductStock_ResultActions'

    const detailsLinkA = document.createElement('a')
    detailsLinkA.className = 'drw-ProductStock_ResultAction'
    detailsLinkA.href = detailsLink
    detailsLinkA.textContent = 'Store details'

    if (imageUrl) {
      imageContainer.appendChild(image)
    }
    actionsDiv.appendChild(detailsLinkA)
    contentDiv.appendChild(addressP)
    contentDiv.appendChild(openingTimeP)
    contentDiv.appendChild(availabilityP)
    contentDiv.appendChild(actionsDiv)

    resultDiv.appendChild(imageContainer)
    resultDiv.appendChild(contentDiv)

    return resultDiv
  }

  get data () {
    // Parse variant data from element
    return JSON.parse(this.els.data.element.innerText)
  }

  get options () {
    // Map our options into something useful
    return this.els.option.elements.map(el => {
      return {
        option: el.dataset.option,
        value: el.value,
      }
    })
  }

  get variant () {
    // Sift through the variant data and find one that matches the selected options
    return (
      this.data.find(variant => {
        return variant.options.every(
          (option, index) => this.options[index].value === option,
        )
      }) || null
    )
  }

  get storeDomain () {
    return this.getAttribute('store-domain')
  }

  get storeAvailabilityString () {
    return this.getAttribute('store-availability')
  }

  get storesAvailabilityString () {
    return this.getAttribute('stores-availability')
  }

  getStoreAvailabilityString (storeCount) {
    const availabilityString =
      storeCount === 1
        ? this.storeAvailabilityString
        : this.storesAvailabilityString

    return availabilityString.replace('{{ store_count }}', storeCount)
  }

  get inStockString () {
    return this.getAttribute('in-stock')
  }

  get lowStockString () {
    return this.getAttribute('low-stock')
  }

  get outOfStockString () {
    return this.getAttribute('out-of-stock')
  }

  getAvailabilityString (quantity) {
    if (!quantity) return this.outOfStockString

    return quantity < this.lowStockThreshold
      ? this.lowStockString
      : this.inStockString
  }

  getStockState (quantity) {
    if (!quantity) return 'outOfStock'

    return quantity < this.lowStockThreshold ? 'lowStock' : 'inStock'
  }

  get lowStockThreshold () {
    return 5
  }

  async fetchCustomersCountryCode () {
    const storageKey = 'location-data'
    const locationDataString = window.sessionStorage.getItem(storageKey)

    if (locationDataString?.length) return JSON.parse(locationDataString)

    const response = await fetch(
      `https://api.ipgeolocation.io/ipgeo?apiKey=${window.theme.ipLocation.apiKey}`,
    )

    const locationData = await response.json()
    window.sessionStorage.setItem(storageKey, JSON.stringify(locationData))

    return locationData
  }
}

if (!customElements.get('product-stock-drawer')) {
  customElements.define('product-stock-drawer', ProductStockDrawer)
}
